import { useToast } from '@chakra-ui/react';

import type { NotificationOutcome } from '@enterprise/api';

import type { ChakraStatus } from './types/chakra-status.type';
import type { NotifierOptions } from './types/notifier-options.type';
import type { UseNotifierResult } from './types/use-notifier-result.type';

import { outcomePresentation } from './outcome-presentation';

const outcomeToStatus = (outcome: NotificationOutcome): ChakraStatus => {
  if (outcome === 'completed') {
    return 'success';
  }

  if (outcome === 'failed') {
    return 'error';
  }

  if (outcome === 'cancelled' || outcome === 'timedout') {
    return 'warning';
  }

  // running | info
  return 'info';
};

export const useNotifier = (): UseNotifierResult => {
  const toast = useToast();

  const show = (status: ChakraStatus, options: NotifierOptions): void => {
    const { description, id, persist, title } = options;

    // persist: true is a toast-suppression/intent marker only (umbrella §7.9).
    // The client never writes notifications; this flag indicates the event is
    // expected to arrive server-backed via SSE, so the synchronous toast is
    // suppressed to avoid duplication.
    if (persist) {
      return;
    }

    if (id !== undefined && toast.isActive(id)) {
      return;
    }

    toast({
      description,
      duration: 4000,
      id,
      isClosable: true,
      status,
      title,
    });
  };

  return {
    error: (options) => {
      show('error', options);
    },
    info: (options) => {
      show('info', options);
    },
    outcome: (outcome, options) => {
      const status = outcomeToStatus(outcome);
      const presentation = outcomePresentation(outcome);

      show(status, { ...options, title: options.title ?? presentation.label });
    },
    success: (options) => {
      show('success', options);
    },
    warning: (options) => {
      show('warning', options);
    },
  };
};
